/**
 * V11.0 — Global Translator (Google Translate REAL Engine)
 *
 * Cerință literală user: "pagina sa poata fi tradusa in toate limbile, printr-un
 * motor de traducere, valabil si real, prin selectarea de limba".
 *
 * Folosim widget-ul oficial Google Translate Element (gratuit, fără API key) —
 * traduce tot textul vizibil din pagină în 100+ limbi, în timp real, prin
 * Google Cloud Translation engine.
 *
 * Strategy:
 * 1. La mount, injectăm scriptul oficial Google Translate (o singură dată).
 * 2. Citim cookie-ul `googtrans` ca să restabilim limba aleasă anterior.
 * 3. La schimbare limbă: setăm cookie + reload, sau triggers programatic
 *    pe select-ul ascuns generat de widget (no-reload).
 * 4. Ascundem banner-ul Google Translate cu CSS pentru un UX curat
 *    (păstrăm credit-ul în footer pentru ToS compliance).
 */
import { useEffect, useState, useRef } from 'react';
import { Globe, ChevronDown, Check, Loader2 } from 'lucide-react';

// 30+ limbi reale acceptate de Google Translate (cu coduri ISO compatibile)
const LANGS = [
  { code: 'ro',    name: 'Română',        flag: '🇷🇴' },
  { code: 'en',    name: 'English',       flag: '🇬🇧' },
  { code: 'es',    name: 'Español',       flag: '🇪🇸' },
  { code: 'fr',    name: 'Français',      flag: '🇫🇷' },
  { code: 'de',    name: 'Deutsch',       flag: '🇩🇪' },
  { code: 'it',    name: 'Italiano',      flag: '🇮🇹' },
  { code: 'pt',    name: 'Português',     flag: '🇵🇹' },
  { code: 'nl',    name: 'Nederlands',    flag: '🇳🇱' },
  { code: 'pl',    name: 'Polski',        flag: '🇵🇱' },
  { code: 'uk',    name: 'Українська',    flag: '🇺🇦' },
  { code: 'ru',    name: 'Русский',       flag: '🇷🇺' },
  { code: 'tr',    name: 'Türkçe',        flag: '🇹🇷' },
  { code: 'ar',    name: 'العربية',         flag: '🇸🇦', rtl: true },
  { code: 'he',    name: 'עברית',           flag: '🇮🇱', rtl: true },
  { code: 'hi',    name: 'हिन्दी',           flag: '🇮🇳' },
  { code: 'zh-CN', name: '中文 (简体)',     flag: '🇨🇳' },
  { code: 'zh-TW', name: '中文 (繁體)',     flag: '🇹🇼' },
  { code: 'ja',    name: '日本語',          flag: '🇯🇵' },
  { code: 'ko',    name: '한국어',          flag: '🇰🇷' },
  { code: 'vi',    name: 'Tiếng Việt',    flag: '🇻🇳' },
  { code: 'th',    name: 'ไทย',            flag: '🇹🇭' },
  { code: 'el',    name: 'Ελληνικά',       flag: '🇬🇷' },
  { code: 'hu',    name: 'Magyar',        flag: '🇭🇺' },
  { code: 'cs',    name: 'Čeština',       flag: '🇨🇿' },
  { code: 'sk',    name: 'Slovenčina',    flag: '🇸🇰' },
  { code: 'bg',    name: 'Български',     flag: '🇧🇬' },
  { code: 'sv',    name: 'Svenska',       flag: '🇸🇪' },
  { code: 'no',    name: 'Norsk',         flag: '🇳🇴' },
  { code: 'fi',    name: 'Suomi',         flag: '🇫🇮' },
  { code: 'da',    name: 'Dansk',         flag: '🇩🇰' },
  { code: 'id',    name: 'Bahasa Indo',   flag: '🇮🇩' },
  { code: 'fa',    name: 'فارسی',          flag: '🇮🇷', rtl: true },
];

const GOOGLE_INCLUDED = LANGS.map(l => l.code).filter(c => c !== 'ro').join(',');

// Read 'googtrans' cookie set by Google Translate
function readGoogtransCookie() {
  const match = document.cookie.match(/googtrans=\/[a-z\-A-Z]+\/([a-z\-A-Z]+)/);
  return match ? match[1] : 'ro';
}

// V12.8 — Robust cookie handling (fixes stuck-language bug).
// Google Translate writes cookies pe host, parent domain AND leading-dot domain.
// If we don't clear ALL variants, subsequent language switches don't take effect.
function domainVariants() {
  const host = window.location.hostname;
  const parts = host.split('.');
  const variants = new Set([host, `.${host}`]);
  // add parent domains progressively: a.b.c.com → b.c.com, c.com
  for (let i = 1; i < parts.length - 1; i++) {
    const parent = parts.slice(i).join('.');
    variants.add(parent);
    variants.add(`.${parent}`);
  }
  return [...variants];
}

function clearGoogtransCookie() {
  // Clear on ALL possible domain scopes — critical for stuck-language bug.
  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `googtrans=;path=/;expires=${past}`;
  domainVariants().forEach((d) => {
    document.cookie = `googtrans=;path=/;domain=${d};expires=${past}`;
  });
}

function setGoogtransCookie(targetLang) {
  // Always clear first — prevents stale cookies from other domain scopes surviving.
  clearGoogtransCookie();
  const value = `/ro/${targetLang}`;
  document.cookie = `googtrans=${value};path=/;max-age=31536000`;
  domainVariants().forEach((d) => {
    document.cookie = `googtrans=${value};path=/;domain=${d};max-age=31536000`;
  });
}

let _scriptInjected = false;

function injectGoogleTranslate() {
  if (_scriptInjected) return;
  if (window.google?.translate) return;
  _scriptInjected = true;

  // Define the init callback BEFORE the script loads
  window.googleTranslateElementInit = function () {
    try {
      // eslint-disable-next-line no-new
      new window.google.translate.TranslateElement({
        pageLanguage: 'ro',
        includedLanguages: GOOGLE_INCLUDED,
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
      }, 'google_translate_element');
    } catch (_e) { /* widget already mounted */ }
  };

  const s = document.createElement('script');
  s.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

export default function GlobalTranslator({ variant = 'dark' }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState('ro');
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Inject widget once per app session
    injectGoogleTranslate();
    setCurrent(readGoogtransCookie());
  }, []);

  useEffect(() => {
    const onClickOut = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  useEffect(() => {
    // RTL handling
    const lang = LANGS.find(l => l.code === current);
    document.documentElement.dir = lang?.rtl ? 'rtl' : 'ltr';
    // Only set lang attribute if not the original (avoid clobbering native ro pages)
    if (current !== 'ro') document.documentElement.lang = current.split('-')[0];
  }, [current]);

  const change = (code) => {
    setBusy(true);
    setOpen(false);
    if (code === 'ro') {
      clearGoogtransCookie();
    } else {
      setGoogtransCookie(code);
    }
    setCurrent(code);
    // Reload to apply translation across full page (most reliable)
    setTimeout(() => window.location.reload(), 200);
  };

  const currentLang = LANGS.find(l => l.code === current) || LANGS[0];

  const buttonClass = variant === 'light'
    ? 'bg-white text-slate-900 hover:bg-slate-100 border border-slate-200'
    : 'bg-white/10 hover:bg-white/20 text-white border border-white/20';

  return (
    <>
      {/* Hidden Google Translate widget mount point */}
      <div id="google_translate_element" style={{ display: 'none', position: 'absolute', top: '-9999px' }} />

      {/* Our custom dropdown UI */}
      <div className="relative" ref={dropdownRef} data-testid="global-translator">
        <button
          onClick={() => setOpen(!open)}
          disabled={busy}
          className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-all backdrop-blur-sm ${buttonClass} disabled:opacity-50`}
          data-testid="global-translator-toggle"
          aria-label="Change language (powered by Google Translate)"
          translate="no"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{currentLang.flag}</span>
          <span className="font-semibold uppercase tracking-wider notranslate">{currentLang.code}</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute right-0 mt-2 w-64 max-h-[70vh] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-2xl z-[60]" translate="no" data-testid="translator-dropdown">
            <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                Tradus de Google Translate
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">100+ limbi · traducere AI live</div>
            </div>
            <div className="p-1">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => change(l.code)}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between gap-2 transition-colors ${
                    l.code === current ? 'bg-violet-50 text-violet-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`translator-lang-${l.code}`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-lg">{l.flag}</span>
                    <div>
                      <div className="text-sm font-semibold leading-tight">{l.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">{l.code}</div>
                    </div>
                  </div>
                  {l.code === current && <Check className="w-4 h-4 text-violet-600" />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export { LANGS };
